import { normaliseError, postJson } from './http';
import { showToast } from './toast';

const DEFAULT_CARD_ENDPOINT = '/carts/ajax/add_cards';

function updateCartCount(amount) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
        const currentAmount = Number.parseInt(element.textContent, 10);

        element.textContent =
            (Number.isFinite(currentAmount) ? currentAmount : 0) + amount;
    });
}

function findSubmitButton(container) {
    return container.querySelector(
        'button[type="submit"], button:not([type])'
    );
}

function setButtonLoading(button, label = 'Toevoegen…') {
    if (!button) {
        return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    button.innerHTML = `
        <span
            class="spinner-border spinner-border-sm"
            aria-hidden="true"
        ></span>
        <span>${label}</span>
    `;
}

function restoreButton(button, originalHtml) {
    if (!button) {
        return;
    }

    button.innerHTML = originalHtml;
    button.disabled = false;
    button.removeAttribute('aria-busy');
}

function getInlineError(container) {
    let errorRegion = container.querySelector('[data-cart-error]');

    if (errorRegion) {
        return errorRegion;
    }

    errorRegion = document.createElement('div');
    errorRegion.className = 'v2-inline-error';
    errorRegion.dataset.cartError = '';
    errorRegion.setAttribute('role', 'alert');
    errorRegion.setAttribute('aria-live', 'assertive');
    errorRegion.hidden = true;

    container.appendChild(errorRegion);

    return errorRegion;
}

function clearInlineError(container) {
    const errorRegion = container.querySelector('[data-cart-error]');

    if (!errorRegion) {
        return;
    }

    errorRegion.textContent = '';
    errorRegion.hidden = true;
}

function showInlineError(container, message) {
    const errorRegion = getInlineError(container);

    errorRegion.textContent = message;
    errorRegion.hidden = false;
}

function getAmount(form) {
    const amountField = form.querySelector('[name="amount"]');
    const parsedAmount = Number.parseInt(amountField?.value || '1', 10);

    return Math.max(1, parsedAmount || 1);
}

function getCardCondition(container) {
    return (
        container.dataset.condition ||
        container.querySelector('[name="card_condition"]')?.value ||
        'NM'
    );
}

async function handleAddToCartForm(event) {
    const form = event.target.closest('[data-add-to-cart]');

    if (!form) {
        return;
    }

    event.preventDefault();

    if (form.dataset.submitting === 'true') {
        return;
    }

    const button = findSubmitButton(form);
    const originalHtml = button?.innerHTML || '';
    const amount = getAmount(form);

    const payload = {
        id: Number(form.dataset.id),
        amount,
    };

    if (form.dataset.kind === 'card') {
        payload.card_condition = getCardCondition(form);
    }

    form.dataset.submitting = 'true';

    clearInlineError(form);
    setButtonLoading(button);

    try {
        await postJson(form.action, payload);

        updateCartCount(amount);

        showToast(
            form.dataset.kind === 'card'
                ? 'Kaart toegevoegd aan je winkelmandje.'
                : 'Product toegevoegd aan je winkelmandje.'
        );

        if (button) {
            button.innerHTML = `
                <i
                    class="bi bi-check-circle-fill"
                    aria-hidden="true"
                ></i>
                <span>Toegevoegd</span>
            `;
        }

        window.setTimeout(() => {
            restoreButton(button, originalHtml);
            form.dataset.submitting = 'false';
        }, 1600);
    } catch (error) {
        const message = normaliseError(error);

        showInlineError(form, message);
        showToast(message, 'error');

        restoreButton(button, originalHtml);
        form.dataset.submitting = 'false';
    }
}

function findQuickAddContainer(button) {
    return button.closest(
        [
            '[data-card-item]',
            '.v2-card-row',
            '.v2-card-tile',
            'article',
            'li',
        ].join(', ')
    );
}

async function handleQuickAdd(event) {
    const button = event.target.closest('[data-quick-add]');

    if (!button) {
        return;
    }

    event.preventDefault();

    if (button.dataset.submitting === 'true') {
        return;
    }

    const originalHtml = button.innerHTML;
    const container = findQuickAddContainer(button);

    button.dataset.submitting = 'true';

    if (container) {
        clearInlineError(container);
    }

    setButtonLoading(button);

    try {
        await postJson(
            button.dataset.url || DEFAULT_CARD_ENDPOINT,
            {
                id: Number(button.dataset.cardId),
                amount: 1,
                card_condition: getCardCondition(button),
            }
        );

        updateCartCount(1);
        showToast('Kaart toegevoegd aan je winkelmandje.');

        button.classList.add('is-added');

        button.innerHTML = `
            <i class="bi bi-check-lg" aria-hidden="true"></i>
            <span>Toegevoegd</span>
        `;

        window.setTimeout(() => {
            button.classList.remove('is-added');

            restoreButton(button, originalHtml);
            button.dataset.submitting = 'false';
        }, 1600);
    } catch (error) {
        const message = normaliseError(error);

        if (container) {
            showInlineError(container, message);
        }

        showToast(message, 'error');

        restoreButton(button, originalHtml);
        button.dataset.submitting = 'false';
    }
}

export function initialiseCart() {
    document.addEventListener('submit', handleAddToCartForm);
    document.addEventListener('click', handleQuickAdd);
}